/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Login Page - Modern split-screen authentication
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, Loader2, Users, Fingerprint, RefreshCw, ShieldAlert } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../stores/authStore'
import { getErrorMessage } from '../utils/errorUtils.js'
import AuthLayout from '../components/auth/AuthLayout'
import MarketingPanel from '../components/auth/MarketingPanel'
import FormInput from '../components/auth/FormInput'
import PasswordInput from '../components/auth/PasswordInput'
import passkeyService from '../services/passkeyService'
import SSOButtons from '../components/auth/SSOButtons'
import versionService from '../services/versionService'
import UpdateAvailableModal from '../components/UpdateAvailableModal'
import { consumeLastPath } from '../services/lastPathService'
import { createLogger } from '../utils/logger'

const logger = createLogger('Login')

// Disable passkey for UI automation/testing (set VITE_DISABLE_PASSKEY=true in .env)
const DISABLE_PASSKEY = import.meta.env.VITE_DISABLE_PASSKEY === 'true'

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false)
  const [registrationEnabled, setRegistrationEnabled] = useState(true)
  const [invitationData, setInvitationData] = useState(null)
  const [passkeyAvailable, setPasskeyAvailable] = useState(false)

  // Version check state
  const [versionUpdate, setVersionUpdate] = useState(null)
  const [showVersionModal, setShowVersionModal] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const passkeyAbortController = useRef(null)

  const { login, loginWithPasskey, checkAuth } = useAuthStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const invitationToken = searchParams.get('token')
  // requireAuthWithRedirect sends disabled users to /login?disabled=1; explain why instead of showing
  // the bare sign-in form. (Inside the SPA, the route guard shows the full AccountDisabled screen.)
  const accountDisabled = searchParams.get('disabled') === '1'

  // Navigate to the last path (if saved from inactivity logout) or dashboard
  const navigateAfterLogin = useCallback(() => {
    const lastPath = consumeLastPath()
    if (lastPath) {
      // Extract pathname only (ignore query params) to prevent redirect loops
      // e.g., /login?token=xyz should still redirect to dashboard
      const pathname = lastPath.split('?')[0]
      if (pathname !== '/login' && pathname !== '/') {
        logger.debug('[Login] Redirecting to last path:', lastPath)
        navigate(lastPath)
        return
      }
    }
    navigate('/dashboard')
  }, [navigate])

  // Check if an update is already in progress (from a previous reload)
  useEffect(() => {
    if (versionService.isUpdateInProgress()) {
      setIsUpdating(true)
      // Give it a moment, then clear if still stuck
      const timeout = setTimeout(() => {
        versionService.clearUpdateState()
        setIsUpdating(false)
      }, 5000)
      return () => clearTimeout(timeout)
    }
  }, [])

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/api/config/public')
        if (response.ok) {
          const config = await response.json()
          setRegistrationEnabled(config.registrationEnabled)
        }
      } catch (error) {
        // Default to showing signup link if config fetch fails
        logger.error('Failed to fetch config:', error)
      }
    }
    fetchConfig()
  }, [])

  // Check for version updates on login page load (mandatory check at login)
  useEffect(() => {
    const checkVersion = async () => {
      try {
        // Initialize version service (not authenticated on login page)
        versionService.init()
        versionService.setAuthenticated(false)

        // Check for updates - will auto-reload if force update needed
        const update = await versionService.checkForUpdate({
          autoUpdateIfLoggedOut: true
        })

        if (update) {
          logger.debug('[Login] Version update available:', update)
          setVersionUpdate(update)

          // For force updates (critical, below minimum, breaking), show modal immediately
          if (update.forceUpdate) {
            setShowVersionModal(true)
          }
        }
      } catch (error) {
        logger.error('[Login] Failed to check version:', error)
        // Don't block login if version check fails
      }
    }

    // Don't check if already updating
    if (!isUpdating) {
      checkVersion()
    }
  }, [isUpdating])

  // Check if passkey authentication is available
  useEffect(() => {
    const checkPasskeySupport = async () => {
      if (DISABLE_PASSKEY) {
        setPasskeyAvailable(false)
        return
      }

      try {
        const isSupported = passkeyService.isSupported()
        const isPlatformAvailable = await passkeyService.isPlatformAuthenticatorAvailable()
        setPasskeyAvailable(isSupported && isPlatformAvailable)
      } catch (error) {
        logger.error('Failed to check passkey support:', error)
        setPasskeyAvailable(false)
      }
    }
    checkPasskeySupport()
  }, [])

  // Fetch invitation details if token is present
  useEffect(() => {
    const fetchInvitation = async () => {
      if (!invitationToken) return

      try {
        const response = await fetch(`/api/invitations/${invitationToken}`)
        if (response.ok) {
          const data = await response.json()
          const invitation = data.invitation || data.data
          setInvitationData(invitation)
          // Pre-fill email from invitation
          if (invitation?.email) {
            setFormData((prev) => ({ ...prev, email: invitation.email }))
          }
        }
      } catch (error) {
        logger.error('Failed to fetch invitation:', error)
      }
    }
    fetchInvitation()
  }, [invitationToken])

  // Start passkey Conditional UI (autofill) on mount
  useEffect(() => {
    if (DISABLE_PASSKEY) return

    const startConditionalUI = async () => {
      try {
        // Check if Conditional UI is available
        const isAvailable = await passkeyService.isConditionalUIAvailable()
        if (!isAvailable) {
          return
        }

        // Create abort controller for cleanup
        passkeyAbortController.current = new AbortController()

        // Start the authentication - this waits for user to select a passkey from autofill
        const result = await passkeyService.authenticateWithConditionalUI()

        if (result.success) {
          // Set user and token in auth store
          await loginWithPasskey(result.user, result.token)
          toast.success('Signed in with passkey')
          navigateAfterLogin()
        }
      } catch (error) {
        // AbortError is expected when user navigates away or submits password form
        if (error.name === 'AbortError' || error.message?.includes('abort')) {
          return
        }
        // NotAllowedError happens when user cancels the passkey prompt
        if (error.name === 'NotAllowedError') {
          return
        }
        // Only log unexpected errors
        logger.error('Passkey authentication error:', error)
      }
    }

    startConditionalUI()

    // Cleanup: abort any pending passkey authentication when component unmounts
    return () => {
      if (passkeyAbortController.current) {
        passkeyAbortController.current.abort()
      }
    }
  }, [navigate, loginWithPasskey])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handlePasskeyLogin = async () => {
    if (DISABLE_PASSKEY || !passkeyAvailable) return

    setIsPasskeyLoading(true)
    setErrors({})

    try {
      const result = await passkeyService.authenticate()

      if (result.success) {
        await loginWithPasskey(result.user, result.token)
        toast.success('Signed in with passkey')

        // Handle invitation if present
        if (invitationToken) {
          try {
            const acceptResponse = await fetch(`/api/invitations/${invitationToken}/accept`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({})
            })
            if (acceptResponse.ok) {
              await checkAuth()
              toast.success(
                `You've joined ${invitationData?.organization?.name || 'the organization'}!`
              )
            }
          } catch (acceptError) {
            logger.error('Error accepting invitation:', acceptError)
          }
        }

        navigateAfterLogin()
      } else {
        toast.error('Passkey authentication failed')
      }
    } catch (error) {
      // NotAllowedError happens when user cancels
      if (error.name === 'NotAllowedError') {
        // User cancelled, no error message needed
        return
      }
      logger.error('Passkey login error:', error)
      toast.error('Failed to sign in with passkey. Please try again or use your password.')
    } finally {
      setIsPasskeyLoading(false)
    }
  }

  // Handle version update
  const handleVersionUpdate = () => {
    setIsUpdating(true)
    versionService.hardReload()
  }

  // Handle dismissing version update (only for non-forced updates)
  const handleDismissVersionUpdate = () => {
    if (versionUpdate?.newBuildId && !versionUpdate?.forceUpdate) {
      versionService.dismissUpdate(versionUpdate.newBuildId)
    }
    setShowVersionModal(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    // Check for forced version updates before login
    if (versionUpdate?.forceUpdate) {
      setShowVersionModal(true)
      return
    }

    setIsLoading(true)

    try {
      const result = await login(formData.email, formData.password)

      if (result.success) {
        // If there's an invitation token, accept it after login
        if (invitationToken) {
          try {
            const acceptResponse = await fetch(`/api/invitations/${invitationToken}/accept`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({})
            })
            if (acceptResponse.ok) {
              // Refresh user data to get updated organizations
              await checkAuth()
              toast.success(
                `You've joined ${invitationData?.organization?.name || 'the organization'}!`
              )
            } else {
              const errorData = await acceptResponse.json()
              logger.error('Failed to accept invitation:', errorData)
              toast.error(errorData.message || 'Failed to accept invitation')
            }
          } catch (acceptError) {
            logger.error('Error accepting invitation:', acceptError)
            // Still navigate to dashboard even if invitation acceptance fails
          }
        }
        navigateAfterLogin()
      } else {
        // Check if email verification is required
        if (
          result.error &&
          typeof result.error === 'object' &&
          result.error.requiresEmailVerification
        ) {
          toast.error('Please verify your email address before logging in')
          navigate('/verify-email', { state: { email: result.error.email || formData.email } })
        } else {
          const errorMessage = getErrorMessage(result.error, 'Login failed')

          // Only clear password on authentication failures (wrong credentials)
          // Keep password for validation errors so user can correct other fields
          const isAuthenticationError =
            errorMessage.toLowerCase().includes('invalid') ||
            errorMessage.toLowerCase().includes('incorrect') ||
            errorMessage.toLowerCase().includes('wrong')

          if (isAuthenticationError) {
            setFormData((prev) => ({
              ...prev,
              password: ''
            }))
            // Focus password field after clearing for better UX
            setTimeout(() => {
              document.getElementById('password')?.focus()
            }, 100)
          }

          toast.error(errorMessage)
          setErrors({ password: errorMessage })
        }
      }
    } catch (error) {
      logger.error('Login error:', error)
      toast.error('An unexpected error occurred. Please try again.')

      // Clear password field on unexpected errors
      setFormData((prev) => ({
        ...prev,
        password: ''
      }))
      setErrors({ password: 'An unexpected error occurred' })

      // Focus password field for better UX
      setTimeout(() => {
        document.getElementById('password')?.focus()
      }, 100)
    }

    setIsLoading(false)
  }

  // Show updating screen while application is refreshing
  if (isUpdating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center p-8"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="inline-block mb-4"
          >
            <RefreshCw className="w-12 h-12 text-primary-600 dark:text-primary-400" />
          </motion.div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Updating Application
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please wait while we load the latest version...
          </p>
        </motion.div>
      </div>
    )
  }

  return (
    <AuthLayout
      marketingContent={<MarketingPanel />}
      formTitle={invitationData ? 'Sign In to Accept Invitation' : 'Welcome Back'}
      formSubtitle={
        invitationData
          ? `You've been invited to join ${invitationData.organization?.name || 'a family'}`
          : 'Sign in to continue your SAT prep journey'
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6" data-1p-ignore>
        {/* Account-disabled notice (redirected here from an HTML route by requireAuthWithRedirect) */}
        {accountDisabled && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 dark:bg-red-900/20 dark:border-red-800">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0 dark:text-red-400" />
              <div className="text-sm">
                <p className="font-medium text-red-900 dark:text-red-200">
                  Your account has been disabled
                </p>
                <p className="text-red-700 mt-1 dark:text-red-300">
                  Access to this account is currently suspended. If you believe this is a mistake,
                  please contact support for assistance.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Invitation Banner */}
        {invitationData && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">You have a pending invitation</p>
                <p className="text-blue-700 mt-1">
                  {invitationData.invitedBy?.firstName
                    ? `${invitationData.invitedBy.firstName} invited you to join as a ${invitationData.role}`
                    : `You've been invited to join as a ${invitationData.role}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* School SSO Buttons (Clever, ClassLink, Google Edu) */}
        <SSOButtons loading={isLoading} disabled={isLoading || isPasskeyLoading} />

        {/* Passkey Sign In Button */}
        {passkeyAvailable && !DISABLE_PASSKEY && (
          <>
            <motion.button
              type="button"
              onClick={handlePasskeyLogin}
              disabled={isLoading || isPasskeyLoading}
              whileHover={{ scale: isLoading || isPasskeyLoading ? 1 : 1.01 }}
              whileTap={{ scale: isLoading || isPasskeyLoading ? 1 : 0.99 }}
              className="w-full flex items-center justify-center px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 text-gray-700 dark:text-gray-200 text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              {isPasskeyLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <Fingerprint className="w-5 h-5 mr-2" />
                  <span>Sign in with Passkey</span>
                </>
              )}
            </motion.button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                  or sign in with email
                </span>
              </div>
            </div>
          </>
        )}

        {/* Email Input */}
        <FormInput
          id="email"
          name="email"
          type="email"
          label="Email Address"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          required
          autoComplete="username webauthn"
          icon={<Mail className="w-5 h-5" />}
          disabled={isLoading || isPasskeyLoading || !!invitationData}
          readOnly={!!invitationData}
        />

        {/* Password Input */}
        <PasswordInput
          id="password"
          name="password"
          label="Password"
          value={formData.password}
          onChange={handleChange}
          error={errors.password}
          required
          autoComplete="current-password webauthn"
          disabled={isLoading || isPasskeyLoading}
          showStrengthMeter={false}
        />

        {/* Forgot Password Link */}
        <div className="flex items-center justify-end">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
          >
            Forgot your password?
          </Link>
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isLoading || isPasskeyLoading}
          whileHover={{ scale: isLoading || isPasskeyLoading ? 1 : 1.01 }}
          whileTap={{ scale: isLoading || isPasskeyLoading ? 1 : 0.99 }}
          className="w-full flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              <span>Signing in...</span>
            </>
          ) : (
            'Sign In'
          )}
        </motion.button>

        {/* Sign Up Link - Only show when open registration is enabled */}
        {registrationEnabled && (
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
              >
                Sign up for free
              </Link>
            </p>
          </div>
        )}
      </form>

      {/* Version Update Modal - shown at login for mandatory version check */}
      <UpdateAvailableModal
        isOpen={showVersionModal}
        onUpdate={handleVersionUpdate}
        onDismiss={versionUpdate?.forceUpdate ? undefined : handleDismissVersionUpdate}
        currentBuildId={versionUpdate?.currentBuildId}
        newBuildId={versionUpdate?.newBuildId}
        updatePriority={versionUpdate?.updatePriority}
        changelog={versionUpdate?.changelog}
      />
    </AuthLayout>
  )
}

export default Login
